import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Chip,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import axios from 'axios';

interface DaySchedule {
  id?: number;
  dayOfWeek: string;
  dayName: string;
  dayNameHindi: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  slotDurationMinutes: number;
  breakStartTime: string | null;
  breakEndTime: string | null;
  maxAppointments: number;
  notes: string | null;
  workingHours: number;
}

interface WeeklySchedule {
  doctorId: number;
  doctorName: string;
  schedule: DaySchedule[];
  totalWorkingDays: number;
  totalWorkingHours: number;
}

const DAYS_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

/**
 * Doctor Availability Management Page
 * Manage weekly schedule with day-wise availability
 */
const DoctorAvailabilityPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState<WeeklySchedule | null>(null);
  const [editedDays, setEditedDays] = useState<Record<string, DaySchedule>>({});
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'GOVERNMENT_DOCTOR') {
      navigate('/login');
      return;
    }
    fetchSchedule();
  }, [isAuthenticated, user, navigate]);

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/schedule`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSchedule(response.data.data);
        // Initialize edited days with current schedule
        const edited: Record<string, DaySchedule> = {};
        response.data.data.schedule.forEach((day: DaySchedule) => {
          edited[day.dayOfWeek] = { ...day };
        });
        setEditedDays(edited);
      }
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDayChange = (dayOfWeek: string, field: string, value: any) => {
    setEditedDays(prev => ({
      ...prev,
      [dayOfWeek]: {
        ...prev[dayOfWeek],
        [field]: value,
      },
    }));
  };

  const handleSaveDay = async (dayOfWeek: string) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const dayData = editedDays[dayOfWeek];

      await axios.put(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/schedule/day`,
        {
          dayOfWeek: dayData.dayOfWeek,
          startTime: dayData.startTime,
          endTime: dayData.endTime,
          isAvailable: dayData.isAvailable,
          slotDurationMinutes: dayData.slotDurationMinutes,
          breakStartTime: dayData.breakStartTime || null,
          breakEndTime: dayData.breakEndTime || null,
          maxAppointments: dayData.maxAppointments,
          notes: dayData.notes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(`${dayData.dayName} schedule saved successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchSchedule();
    } catch (error) {
      console.error('Failed to save day schedule:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const scheduleList = DAYS_ORDER.map(day => editedDays[day]).filter(Boolean);

      await axios.put(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/schedule`,
        { schedule: scheduleList },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage('Weekly schedule saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchSchedule();
    } catch (error) {
      console.error('Failed to save schedule:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/schedule/default`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSchedule(response.data.data);
        setSuccessMessage('Default schedule set (Mon-Fri, 9:00-17:00)');
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchSchedule();
      }
    } catch (error) {
      console.error('Failed to set default schedule:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAvailability = async (dayOfWeek: string) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.patch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/schedule/toggle/${dayOfWeek}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchSchedule();
    } catch (error) {
      console.error('Failed to toggle availability:', error);
    }
  };

  const getDayColor = (day: DaySchedule) => {
    if (!day.isAvailable) return '#f5f5f5';
    const dayNum = DAYS_ORDER.indexOf(day.dayOfWeek);
    if (dayNum < 5) return '#e8f5e9'; // Weekdays - green
    return '#fff3e0'; // Weekends - orange
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'transparent' , minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Availability Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Set your weekly schedule and availability for appointments
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchSchedule}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<RestoreIcon />}
            onClick={handleSetDefault}
            disabled={saving}
          >
            Set Default
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveAll}
            disabled={saving}
          >
            Save All
          </Button>
        </Box>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Summary Cards */}
      {schedule && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#1D4ED8', mx: 'auto', mb: 1, border: '2px solid rgba(29, 78, 216, 0.2)' }}>
                  <CalendarIcon />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>{schedule.totalWorkingDays}</Typography>
                <Typography variant="body2" color="text.secondary">Working Days</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#15803D', mx: 'auto', mb: 1, border: '2px solid rgba(21, 128, 61, 0.2)' }}>
                  <TimeIcon />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>{schedule.totalWorkingHours}</Typography>
                <Typography variant="body2" color="text.secondary">Total Hours/Week</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#B45309', mx: 'auto', mb: 1, border: '2px solid rgba(180, 83, 9, 0.2)' }}>
                  <ScheduleIcon />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>
                  {schedule.schedule?.filter((d: DaySchedule) => d.isAvailable).length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Available Days</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#DC2626', mx: 'auto', mb: 1, border: '2px solid rgba(220, 38, 38, 0.2)' }}>
                  <CancelIcon />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>
                  {schedule.schedule?.filter((d: DaySchedule) => !d.isAvailable).length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Off Days</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Weekly Schedule Grid */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon color="primary" />
          Weekly Schedule
        </Typography>

        <Grid container spacing={2}>
          {DAYS_ORDER.map((dayKey) => {
            const day = editedDays[dayKey] || schedule?.schedule?.find((d: DaySchedule) => d.dayOfWeek === dayKey);
            if (!day) return null;

            return (
              <Grid item xs={12} md={6} key={dayKey}>
                <Card
                  sx={{
                    borderRadius: 2,
                    bgcolor: getDayColor(day),
                    border: day.isAvailable ? '1px solid #e0e0e0' : '1px dashed #bdbdbd',
                    opacity: day.isAvailable ? 1 : 0.7,
                  }}
                >
                  <CardContent>
                    {/* Day Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {day.dayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {day.dayNameHindi}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {day.isAvailable ? (
                          <Chip
                            icon={<CheckIcon />}
                            label={`${day.workingHours} hours`}
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<CancelIcon />}
                            label="Off"
                            color="default"
                            size="small"
                          />
                        )}
                        <FormControlLabel
                          control={
                            <Switch
                              checked={day.isAvailable}
                              onChange={() => {
                                handleDayChange(dayKey, 'isAvailable', !day.isAvailable);
                              }}
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>
                    </Box>

                    {/* Time Fields */}
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="time"
                          label="Start Time"
                          value={day.startTime}
                          onChange={(e) => handleDayChange(dayKey, 'startTime', e.target.value)}
                          disabled={!day.isAvailable}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="time"
                          label="End Time"
                          value={day.endTime}
                          onChange={(e) => handleDayChange(dayKey, 'endTime', e.target.value)}
                          disabled={!day.isAvailable}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Slot Duration (min)"
                          value={day.slotDurationMinutes}
                          onChange={(e) => handleDayChange(dayKey, 'slotDurationMinutes', parseInt(e.target.value))}
                          disabled={!day.isAvailable}
                          size="small"
                          inputProps={{ min: 15, max: 60 }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Max Appointments"
                          value={day.maxAppointments}
                          onChange={(e) => handleDayChange(dayKey, 'maxAppointments', parseInt(e.target.value))}
                          disabled={!day.isAvailable}
                          size="small"
                          inputProps={{ min: 1, max: 20 }}
                        />
                      </Grid>
                    </Grid>

                    {/* Save Button */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={() => handleSaveDay(dayKey)}
                        disabled={saving}
                      >
                        Save
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Quick Actions */}
      <Paper sx={{ p: 3, mt: 3, borderRadius: 2, bgcolor: 'transparent' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Quick Actions</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={() => {
              DAYS_ORDER.slice(0, 5).forEach(day => {
                handleDayChange(day, 'isAvailable', true);
                handleDayChange(day, 'startTime', '09:00');
                handleDayChange(day, 'endTime', '17:00');
              });
              DAYS_ORDER.slice(5).forEach(day => {
                handleDayChange(day, 'isAvailable', false);
              });
            }}
          >
            Set Weekdays Only (Mon-Fri)
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              DAYS_ORDER.forEach(day => {
                handleDayChange(day, 'isAvailable', true);
                handleDayChange(day, 'startTime', '09:00');
                handleDayChange(day, 'endTime', '17:00');
              });
            }}
          >
            Set All Days Available
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              DAYS_ORDER.forEach(day => {
                handleDayChange(day, 'isAvailable', false);
              });
            }}
          >
            Clear All
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default DoctorAvailabilityPage;
